/**
 * RouteBottomSheet Component
 *
 * A draggable bottom sheet for previewing and interacting with route details.
 * Features collapsed and expanded states with climb logging functionality.
 *
 * Features:
 * - Drag handle for expansion/collapse
 * - Colored header bar (route color)
 * - Grade display with completion status
 * - Quick log climb action (Send/Attempt)
 * - Expandable for full route details
 *
 * @example
 * ```tsx
 * <RouteBottomSheet
 *   route={selectedRoute}
 *   colorGrades={gym.colorGrades}
 *   isSent={sentRouteIds.has(route.id)}
 *   onClose={() => setSelectedRoute(null)}
 *   onLogClimb={(routeId, outcome) => logClimb(routeId, outcome)}
 * />
 * ```
 */

import { useState, useRef, useEffect, type CSSProperties, type TouchEvent } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import StarIcon from '@mui/icons-material/Star';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { ViewMode } from './ClimbBottomBar';
import type { Route, ColorGrade, ClimbOutcome, VGrade, GradeModifier } from '@flash/shared';
import { PhotoLightbox } from './PhotoLightbox';
import { StarRatingInline } from './StarRatingInline';
import { RouteStylesDisplay } from './RouteStylesDisplay';
import { RouteCircle } from '@/components/icons/RouteCircle';
import { useQualityRating } from '@/hooks/useQualityRating';
import { useRouteClimbHistory } from '@/hooks/useRouteClimbHistory';
import { useRoute } from '@/hooks/useRoute';

/**
 * Format a grade with optional modifier for display
 */
const formatGrade = (grade: VGrade, modifier?: GradeModifier | null): string => {
  if (!modifier) return grade;
  return `${grade}${modifier}`;
};

/**
 * Props for RouteBottomSheet component
 */
export interface RouteBottomSheetProps {
  /** Route to display */
  route: Route;
  /** Gym's color grades for styling */
  colorGrades: ColorGrade[];
  /** Whether the user has sent this route (kept for backwards compatibility) */
  isSent: boolean;
  /** Called when sheet is closed */
  onClose: () => void;
  /** Called when user logs a climb */
  onLogClimb: (routeId: string, outcome: ClimbOutcome, count?: number) => Promise<void>;
  /** Called when user unsends a route */
  onUnsend?: (routeId: string) => Promise<void>;
  /** Called when user deletes all climb data for this route */
  onDeleteClimbData?: (routeId: string) => Promise<void>;
  /** Whether a climb is being logged */
  isLogging?: boolean;
  /** Current view mode (for toggle in header) */
  viewMode?: ViewMode;
  /** Called when view mode changes (for toggle in header) */
  onViewModeChange?: (mode: ViewMode) => void;
  /** Whether the gym has a floor plan (to enable/disable map view toggle) */
  hasFloorPlan?: boolean;
}

/**
 * Sheet expansion state
 */
type SheetState = 'collapsed' | 'expanded';

/**
 * Format date for display
 */
const formatDate = (date: Date | { seconds: number }): string => {
  const d = date instanceof Date ? date : new Date((date as { seconds: number }).seconds * 1000);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * RouteBottomSheet Component
 */
export const RouteBottomSheet = ({
  route,
  colorGrades,
  onClose,
  onLogClimb,
  onDeleteClimbData,
  isLogging = false,
  viewMode,
  onViewModeChange,
  hasFloorPlan = false,
}: RouteBottomSheetProps) => {
  const [sheetState, setSheetState] = useState<SheetState>('collapsed');
  const [isVisible, setIsVisible] = useState(false);
  const [logSuccess, setLogSuccess] = useState<ClimbOutcome | null>(null);
  const [sendCount, setSendCount] = useState(1);
  const [attemptCount, setAttemptCount] = useState(1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [lightboxPhotoIndex, setLightboxPhotoIndex] = useState<number | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number>(0);
  const startTranslateY = useRef<number>(0);
  const stepperIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const stepperTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLoggingRef = useRef<boolean>(false);

  // Quality rating hook
  const {
    rating: userQualityRating,
    submitRating: submitQualityRating,
    removeRating: removeQualityRating,
    isSubmitting: isSubmittingRating,
  } = useQualityRating(route.id);

  // Personal climb history for this route
  const { sendsCount, attemptsCount } = useRouteClimbHistory(route.id);

  // Real-time route data for stats updates
  const { route: liveRoute } = useRoute(route.id);
  const displayRating = liveRoute?.averageRating ?? route.averageRating;
  const displayRatingCount = liveRoute?.ratingCount ?? route.ratingCount;
  const displayTotalSends = liveRoute?.totalSends ?? route.totalSends;

  // Get hold and tag colors from identification
  const holdColorHex = route.identification?.holdColor
    ? (colorGrades.find((cg) => cg.displayName === route.identification?.holdColor)?.color ?? null)
    : null;
  const tagColorHex = route.identification?.tagColor
    ? (colorGrades.find((cg) => cg.displayName === route.identification?.tagColor)?.color ?? null)
    : null;

  // Get route color from color grades (fallback for legacy routes)
  const routeColor =
    holdColorHex ??
    colorGrades.find((cg) => cg.displayName === route.colorGrade)?.color ??
    '#808080';

  // Animate in on mount
  useEffect(() => {
    const timeout = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timeout);
  }, []);

  // Handle close with animation
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  // Toggle sheet expansion
  const toggleExpand = () => {
    setSheetState((prev) => (prev === 'collapsed' ? 'expanded' : 'collapsed'));
  };

  // Handle quick log climb
  const handleLogClimb = async (outcome: ClimbOutcome) => {
    // Guard against rapid double-clicks
    if (isLoggingRef.current || isLogging) return;
    isLoggingRef.current = true;

    try {
      const count = outcome === 'send' ? sendCount : attemptCount;
      await onLogClimb(route.id, outcome, count);
      setLogSuccess(outcome);
      // Reset count after logging
      if (outcome === 'send') setSendCount(1);
      if (outcome === 'attempt') setAttemptCount(1);
      setTimeout(() => setLogSuccess(null), 2000);
    } catch {
      // Error handled by parent
    } finally {
      isLoggingRef.current = false;
    }
  };

  // Handle delete climb data
  const handleDeleteClimbData = async () => {
    if (!onDeleteClimbData || isDeleting) return;
    setIsDeleting(true);
    try {
      await onDeleteClimbData(route.id);
      setShowDeleteConfirm(false);
    } catch {
      // Error handled by parent
    } finally {
      setIsDeleting(false);
    }
  };

  // Stepper long-press acceleration
  const MAX_COUNT = 20;

  const clearStepperTimers = () => {
    if (stepperTimeoutRef.current) {
      clearTimeout(stepperTimeoutRef.current);
      stepperTimeoutRef.current = null;
    }
    if (stepperIntervalRef.current) {
      clearInterval(stepperIntervalRef.current);
      stepperIntervalRef.current = null;
    }
  };

  const handleStepperHoldStart = (
    direction: 'up' | 'down',
    setter: React.Dispatch<React.SetStateAction<number>>
  ) => {
    // Immediate first step happens via onClick
    // After 400ms, start accelerating
    stepperTimeoutRef.current = setTimeout(() => {
      stepperIntervalRef.current = setInterval(() => {
        setter((c) => (direction === 'up' ? Math.min(MAX_COUNT, c + 1) : Math.max(1, c - 1)));
      }, 100); // Fast repeat every 100ms
    }, 400);
  };

  const handleStepperHoldEnd = () => {
    clearStepperTimers();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStepperTimers();
    };
  }, []);

  // Drag handling for touch
  const handleTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      dragStartY.current = touch.clientY;
      startTranslateY.current = 0;
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;

    const deltaY = touch.clientY - dragStartY.current;

    // Allow dragging down to close, or up to expand
    if (deltaY > 50 && sheetState === 'collapsed') {
      handleClose();
    } else if (deltaY < -50 && sheetState === 'collapsed') {
      setSheetState('expanded');
    } else if (deltaY > 50 && sheetState === 'expanded') {
      setSheetState('collapsed');
    }
  };

  const handleTouchEnd = () => {
    dragStartY.current = 0;
    startTranslateY.current = 0;
  };

  // Calculate sheet height
  const sheetHeight = sheetState === 'collapsed' ? '40vh' : '80vh';

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          ...backdropStyle,
          opacity: isVisible ? 1 : 0,
        }}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        style={{
          ...sheetStyle,
          height: sheetHeight,
          transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="dialog"
        aria-modal="true"
        aria-label={`Route details: ${route.name || (route.officialGrade ? formatGrade(route.officialGrade, route.officialGradeModifier) : route.colorGrade)}`}
      >
        {/* Drag Handle */}
        <div
          style={dragHandleContainerStyle}
          onClick={toggleExpand}
          role="button"
          aria-label={sheetState === 'collapsed' ? 'Expand details' : 'Collapse details'}
        >
          <div style={dragHandleStyle} />
        </div>

        {/* Header */}
        <div
          style={headerStyle}
          onClick={toggleExpand}
          role="button"
          aria-expanded={sheetState === 'expanded'}
        >
          <div style={headerTopRowStyle}>
            <div style={gradeStatsStyle}>
              {/* Route Circle */}
              <RouteCircle
                grade={
                  route.officialGrade
                    ? formatGrade(route.officialGrade, route.officialGradeModifier)
                    : ''
                }
                holdColor={routeColor}
                tagColor={tagColorHex ?? undefined}
                size={40}
              />
              {/* Title: route name or wall section */}
              <div style={headerTitleGroupStyle}>
                <span
                  style={{
                    ...gradeTextStyle,
                    ...(sheetState === 'collapsed'
                      ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                      : { whiteSpace: 'normal' }),
                  }}
                >
                  {route.name || route.wallSection}
                </span>
                <div style={statsLineStyle}>
                  <span style={personalStatsStyle}>
                    You: {sendsCount}/{attemptsCount}
                  </span>
                  <span style={statDotStyle}>·</span>
                  <span>{displayTotalSends} total</span>
                  <span style={statDotStyle}>·</span>
                  <span style={headerRatingStyle}>
                    <StarIcon style={headerStarStyle} />
                    {displayRating != null
                      ? `${displayRating.toFixed(1)} (${displayRatingCount})`
                      : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Header Actions - close + toggle (toggle rightmost to match FAB position) */}
            <div style={headerActionsStyle} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={handleClose}
                style={cardCloseButtonStyle}
                aria-label="Close route details"
              >
                <CloseIcon />
              </button>
              {/* View toggle - rightmost to match FAB position for seamless transition */}
              {onViewModeChange && viewMode && (
                <button
                  onClick={() => onViewModeChange(viewMode === 'list' ? 'map' : 'list')}
                  style={cardToggleButtonStyle}
                  disabled={viewMode === 'list' && !hasFloorPlan}
                  aria-label={viewMode === 'list' ? 'Switch to map view' : 'Switch to list view'}
                >
                  {viewMode === 'list' ? <MapIcon /> : <FormatListBulletedIcon />}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {/* Log Actions - Stacked layout */}
          <div style={logActionsStackedStyle}>
            {/* Row 1: Log Sends stepper */}
            <div style={logRowStyle}>
              <span style={logLabelStyle}>Log Sends</span>
              <div style={stepperGroupStyle}>
                <div style={triesStepperStyle}>
                  <button
                    onClick={() => setSendCount((c) => Math.max(1, c - 1))}
                    onMouseDown={() => handleStepperHoldStart('down', setSendCount)}
                    onMouseUp={handleStepperHoldEnd}
                    onMouseLeave={handleStepperHoldEnd}
                    onTouchStart={() => handleStepperHoldStart('down', setSendCount)}
                    onTouchEnd={handleStepperHoldEnd}
                    style={triesStepperButtonStyle}
                    disabled={sendCount <= 1 || isLogging}
                    aria-label="Decrease send count"
                  >
                    −
                  </button>
                  <span style={triesStepperValueStyle}>{sendCount}</span>
                  <button
                    onClick={() => setSendCount((c) => Math.min(MAX_COUNT, c + 1))}
                    onMouseDown={() => handleStepperHoldStart('up', setSendCount)}
                    onMouseUp={handleStepperHoldEnd}
                    onMouseLeave={handleStepperHoldEnd}
                    onTouchStart={() => handleStepperHoldStart('up', setSendCount)}
                    onTouchEnd={handleStepperHoldEnd}
                    style={triesStepperButtonStyle}
                    disabled={sendCount >= MAX_COUNT || isLogging}
                    aria-label="Increase send count"
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={() => handleLogClimb('send')}
                  disabled={isLogging}
                  style={{
                    ...logSendButtonStyle,
                    ...(logSuccess === 'send' ? logButtonSuccessStyle : {}),
                  }}
                >
                  {logSuccess === 'send' ? '✓' : 'Log'}
                </button>
              </div>
            </div>

            {/* Row 2: Log Attempts stepper */}
            <div style={logRowStyle}>
              <span style={logLabelStyle}>Log Attempts</span>
              <div style={stepperGroupStyle}>
                <div style={triesStepperStyle}>
                  <button
                    onClick={() => setAttemptCount((c) => Math.max(1, c - 1))}
                    onMouseDown={() => handleStepperHoldStart('down', setAttemptCount)}
                    onMouseUp={handleStepperHoldEnd}
                    onMouseLeave={handleStepperHoldEnd}
                    onTouchStart={() => handleStepperHoldStart('down', setAttemptCount)}
                    onTouchEnd={handleStepperHoldEnd}
                    style={triesStepperButtonStyle}
                    disabled={attemptCount <= 1 || isLogging}
                    aria-label="Decrease attempt count"
                  >
                    −
                  </button>
                  <span style={triesStepperValueStyle}>{attemptCount}</span>
                  <button
                    onClick={() => setAttemptCount((c) => Math.min(MAX_COUNT, c + 1))}
                    onMouseDown={() => handleStepperHoldStart('up', setAttemptCount)}
                    onMouseUp={handleStepperHoldEnd}
                    onMouseLeave={handleStepperHoldEnd}
                    onTouchStart={() => handleStepperHoldStart('up', setAttemptCount)}
                    onTouchEnd={handleStepperHoldEnd}
                    style={triesStepperButtonStyle}
                    disabled={attemptCount >= MAX_COUNT || isLogging}
                    aria-label="Increase attempt count"
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={() => handleLogClimb('attempt')}
                  disabled={isLogging}
                  style={{
                    ...logAttemptButtonStyle,
                    ...(logSuccess === 'attempt' ? logButtonSuccessStyle : {}),
                  }}
                >
                  {logSuccess === 'attempt' ? '✓' : 'Log'}
                </button>
              </div>
            </div>
          </div>

          {/* Expanded Content */}
          {sheetState === 'expanded' && (
            <div style={expandedContentStyle}>
              {/* Quality Rating */}
              <div style={infoSectionStyle}>
                <StarRatingInline
                  userRating={userQualityRating?.rating ?? null}
                  averageRating={displayRating}
                  onRate={submitQualityRating}
                  onClearRating={removeQualityRating}
                  isSubmitting={isSubmittingRating}
                />
              </div>

              {/* Photos */}
              {route.photos.length > 0 && (
                <div style={infoSectionStyle}>
                  <h4 style={sectionTitleStyle}>Photos</h4>
                  <div style={photoGridStyle}>
                    {route.photos.map((photo, index) => (
                      <img
                        key={index}
                        src={photo.thumbnailUrl || photo.url}
                        alt={`Route photo ${index + 1}`}
                        style={photoThumbnailStyle}
                        onClick={() => setLightboxPhotoIndex(index)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            setLightboxPhotoIndex(index);
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Description - moved up */}
              {route.description && (
                <div style={infoSectionStyle}>
                  <h4 style={sectionTitleStyle}>Description</h4>
                  <p style={descriptionStyle}>{route.description}</p>
                </div>
              )}

              {/* Styles, Holds, Skills */}
              {route.styles.length > 0 && <RouteStylesDisplay styles={route.styles} />}

              {/* Route Info Section - Wall section only */}
              <div style={infoSectionStyle}>
                <h4 style={sectionTitleStyle}>Route Info</h4>
                <div style={wallSectionCardStyle}>
                  <span style={wallSectionLabelStyle}>Wall Section</span>
                  <span style={wallSectionValueStyle}>{route.wallSection}</span>
                </div>
              </div>

              {/* Setters - Simplified */}
              <div style={infoSectionStyle}>
                <h4 style={sectionTitleStyle}>Setters</h4>
                <div style={settersSimpleCardStyle}>
                  <span style={settersNamesStyle}>
                    {route.setters && route.setters.length > 0
                      ? route.setters.map((s) => s.name).join(', ')
                      : route.setterName || 'Unknown'}
                  </span>
                  <span style={settersDateStyle}>
                    Set: {formatDate(route.setDate)}
                    {route.retiredAt ? ` • Retired: ${formatDate(route.retiredAt)}` : ''}
                  </span>
                </div>
              </div>

              {/* Delete Climb Data */}
              {onDeleteClimbData && (sendsCount > 0 || attemptsCount > 0) && (
                <div style={dangerSectionStyle}>
                  {!showDeleteConfirm ? (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      style={deleteButtonStyle}
                      aria-label="Delete climb data for this route"
                    >
                      <DeleteOutlineIcon style={{ fontSize: '1.125rem' }} />
                      Delete my climb data
                    </button>
                  ) : (
                    <div style={deleteConfirmStyle}>
                      <p style={deleteConfirmTextStyle}>
                        Delete all {sendsCount} sends and {attemptsCount} attempts for this route?
                      </p>
                      <div style={deleteConfirmButtonsStyle}>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          style={cancelButtonStyle}
                          disabled={isDeleting}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteClimbData}
                          style={confirmDeleteButtonStyle}
                          disabled={isDeleting}
                        >
                          {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Photo Lightbox */}
      {lightboxPhotoIndex !== null && (
        <PhotoLightbox
          photos={route.photos}
          initialIndex={lightboxPhotoIndex}
          onClose={() => setLightboxPhotoIndex(null)}
        />
      )}
    </>
  );
};

// Styles
const backdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  transition: 'opacity 300ms ease-out',
  zIndex: 999,
};

const sheetStyle: CSSProperties = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'var(--color-bg-primary, white)',
  borderTopLeftRadius: '16px',
  borderTopRightRadius: '16px',
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
  transition: 'transform 300ms ease-out, height 300ms ease-out',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const dragHandleContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  padding: '12px 0 8px',
  cursor: 'pointer',
};

const dragHandleStyle: CSSProperties = {
  width: '40px',
  height: '4px',
  backgroundColor: 'var(--color-border, #e5e7eb)',
  borderRadius: '2px',
};

const headerStyle: CSSProperties = {
  padding: '12px 20px 16px',
  position: 'relative',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const headerTopRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
};

const gradeStatsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: 1,
  minWidth: 0,
};

const headerTitleGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
  flex: 1,
};

const gradeTextStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 700,
  lineHeight: 1.2,
  color: 'var(--color-text-primary, #1f2937)',
};

const statsLineStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary, #6b7280)',
  flexWrap: 'wrap',
};

const statDotStyle: CSSProperties = {
  opacity: 0.5,
};

const personalStatsStyle: CSSProperties = {
  fontWeight: 600,
  color: 'var(--color-text-primary, #374151)',
};

const headerRatingStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  whiteSpace: 'nowrap',
};

const headerStarStyle: CSSProperties = {
  color: '#FBBF24',
  fontSize: '0.875rem',
};

const headerActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
};

// Card toggle button - matches FAB style for visual continuity
const cardToggleButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '44px',
  height: '44px',
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
  color: 'var(--color-text-primary, #1f2937)',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
};

// Card close button - circular to match toggle
const cardCloseButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '44px',
  height: '44px',
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
  color: 'var(--color-text-primary, #1f2937)',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
};

const contentStyle: CSSProperties = {
  flex: 1,
  padding: '12px 20px 24px',
  overflowY: 'auto',
};

// Stacked layout for log actions
const logActionsStackedStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

// Log row style (sends or attempts)
const logRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  borderRadius: '12px',
};

const logLabelStyle: CSSProperties = {
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

const stepperGroupStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const triesStepperStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const triesStepperButtonStyle: CSSProperties = {
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.25rem',
  fontWeight: 600,
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-primary, white)',
  color: 'var(--color-text-primary, #1f2937)',
  cursor: 'pointer',
};

const triesStepperValueStyle: CSSProperties = {
  minWidth: '28px',
  textAlign: 'center',
  fontSize: '1.125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

const logSendButtonStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 600,
  border: 'none',
  borderRadius: '8px',
  backgroundColor: '#22c55e',
  color: 'white',
  cursor: 'pointer',
};

const logAttemptButtonStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 600,
  border: 'none',
  borderRadius: '8px',
  backgroundColor: '#3b82f6',
  color: 'white',
  cursor: 'pointer',
};

const logButtonSuccessStyle: CSSProperties = {
  backgroundColor: '#16a34a',
};

const expandedContentStyle: CSSProperties = {
  marginTop: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const infoSectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const sectionTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.9375rem',
  fontWeight: 700,
  color: 'var(--color-text-primary, #1f2937)',
};

const descriptionStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: 'var(--color-text-primary, #1f2937)',
  lineHeight: 1.5,
};

const photoGridStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  overflowX: 'auto',
  paddingBottom: '8px',
};

const photoThumbnailStyle: CSSProperties = {
  width: '120px',
  height: '120px',
  objectFit: 'cover',
  borderRadius: '8px',
  flexShrink: 0,
  cursor: 'pointer',
};

// Wall section card style
const wallSectionCardStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  borderRadius: '8px',
};

const wallSectionLabelStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

const wallSectionValueStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 400,
  color: 'var(--color-text-secondary, #6b7280)',
};

// Simplified setter card styles
const settersSimpleCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '12px',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  borderRadius: '8px',
};

const settersNamesStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

const settersDateStyle: CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

// Delete climb data styles
const dangerSectionStyle: CSSProperties = {
  marginTop: '8px',
  paddingTop: '16px',
  borderTop: '1px solid var(--color-border, #e5e7eb)',
};

const deleteButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  width: '100%',
  padding: '12px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  border: '1px solid #fecaca',
  borderRadius: '8px',
  backgroundColor: 'transparent',
  color: '#dc2626',
  cursor: 'pointer',
};

const deleteConfirmStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '16px',
  backgroundColor: '#fef2f2',
  borderRadius: '8px',
  border: '1px solid #fecaca',
};

const deleteConfirmTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: '#991b1b',
  textAlign: 'center',
};

const deleteConfirmButtonsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const cancelButtonStyle: CSSProperties = {
  flex: 1,
  padding: '10px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-primary, white)',
  color: 'var(--color-text-primary, #1f2937)',
  cursor: 'pointer',
};

const confirmDeleteButtonStyle: CSSProperties = {
  flex: 1,
  padding: '10px 16px',
  fontSize: '0.875rem',
  fontWeight: 600,
  border: 'none',
  borderRadius: '8px',
  backgroundColor: '#dc2626',
  color: 'white',
  cursor: 'pointer',
};

export default RouteBottomSheet;
